import { ref, watch } from 'vue';
import { useOnline } from '@vueuse/core';
import { create, SCClientSocket } from 'socketcluster-client';
import { useUser } from '@core/user';

import { dataLayer } from '@libs/gtm';

import { useCallback } from './useCallback';
import { createSocketConfig } from '../configs/socket';

const socket = ref<SCClientSocket>();
const connected = ref(false);
const online = useOnline();

watch(online, (value) => {
  if (value) {
    socket.value?.reconnect();
  } else {
    connected.value = false;
    socket.value?.disconnect();
  }
});

function createConnection(user: string): SCClientSocket {
  return create(createSocketConfig(user));
}

export function useSocket() {
  const onReady = useCallback();
  const { user, onUserReady } = useUser();

  onUserReady(() => {
    if (!socket.value) {
      if (user.value?.userName) {
        socket.value = createConnection(user.value?.userName);
      }

      socket.value?.on('connect', () => {
        dataLayer.push({
          event: 'socketCluster',
          socketConnectionState: 'connect',
        });

        connected.value = true;
      });

      // This is a catch-all event for both 'disconnect' and 'connectAbort'
      socket.value?.on('close', () => {
        dataLayer.push({
          event: 'socketCluster',
          socketConnectionState: 'disconnect',
        });

        connected.value = false;
      });
    }
  });

  watch([socket, onReady.callback], ([isSocketReady, callback]) => {
    if (isSocketReady && callback) {
      onReady.trigger({ user });
    }
  });

  return { socket, onReady: onReady.on, connected };
}
